export type BlogCategoryId =
  | 'claims-advice'
  | 'evidence'
  | 'council-data'
  | 'rejections'
  | 'road-maintenance';

export interface BlogCategory {
  id: BlogCategoryId;
  title: string;
  description: string;
  searchIntent: string;
}

export interface BlogPostSummary {
  slug: string;
  title: string;
  description: string;
  category: BlogCategoryId;
  readTime: string;
  datePublished: string;
  dateModified: string;
  keywords: string[];
  geoFocus: string[];
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
}

export const blogCategories: BlogCategory[] = [
  {
    id: 'claims-advice',
    title: 'Pothole Claim Advice',
    description: 'Plain-English help for deciding whether a pothole damage claim is worth pursuing.',
    searchIntent: 'Can I claim for pothole damage?',
  },
  {
    id: 'evidence',
    title: 'Evidence and Documentation',
    description: 'Practical ways to capture, organise, and explain the evidence councils ask for.',
    searchIntent: 'What evidence do I need for a pothole claim?',
  },
  {
    id: 'council-data',
    title: 'Council Data',
    description: 'DfT ratings, inspection standards, and local authority road maintenance context.',
    searchIntent: 'How good is my council at maintaining roads?',
  },
  {
    id: 'rejections',
    title: 'Rejected Claims',
    description: 'How to understand council responses, Section 58 defences, and escalation options.',
    searchIntent: 'What should I do if my pothole claim is rejected?',
  },
  {
    id: 'road-maintenance',
    title: 'Road Maintenance',
    description: 'Background on highway duties, repair thresholds, and how potholes are prioritised.',
    searchIntent: 'Who is responsible for fixing potholes?',
  },
];

// Add future articles here after creating their matching route and page under /blog.
export const blogPosts: BlogPostSummary[] = [
  {
    slug: 'summer-road-trips-pothole-damage',
    title: 'Summer Road Trips and Pothole Damage: What to Check Before You Drive Away',
    description:
      'Before a summer road trip, check for pothole damage that can become dangerous under heat, load, and motorway speeds. Use this tyre, suspension, and evidence checklist.',
    category: 'claims-advice',
    readTime: '6 min',
    datePublished: '2026-06-14',
    dateModified: '2026-06-14',
    keywords: [
      'summer road trip car check',
      'pothole damage before long drive',
      'tyre sidewall bulge',
      'loaded car tyre pressure',
      'pothole damage claim',
    ],
    geoFocus: ['England', 'Wales', 'UK motorways', 'UK local roads'],
    image: '/blog/summer-road-trips-pothole-damage-1200.webp',
    imageAlt:
      'Driver checking a front tyre before a summer road trip with luggage loaded in the boot.',
    imageWidth: 1200,
    imageHeight: 630,
  },
  {
    slug: 'mechanics-view-hidden-pothole-damage',
    title: "The Mechanic's View: The Hidden Damage Drivers Miss After Hitting a Pothole",
    description:
      'A qualified mechanic explains the hidden pothole damage drivers miss, from sidewall bulges to suspension and steering, and how to protect a claim.',
    category: 'evidence',
    readTime: '7 min',
    datePublished: '2026-05-03',
    dateModified: '2026-05-03',
    keywords: [
      'hidden pothole damage',
      'mechanic pothole advice',
      'sidewall bulge',
      'wheel alignment damage',
      'suspension and steering damage',
    ],
    geoFocus: ['England', 'Wales', 'Dudley', 'Stourbridge', 'Halesowen'],
    image: '/blog/mechanics-view-hidden-pothole-damage-1200.webp',
    imageAlt:
      'Mechanic pointing to a tyre sidewall bulge on a car wheel, showing hidden pothole impact damage drivers can miss.',
    imageWidth: 1200,
    imageHeight: 630,
  },
  {
    slug: 'council-rejected-your-pothole-claim',
    title: 'Council Rejected Your Pothole Claim? What to Check Before Giving Up',
    description:
      'A council pothole claim rejection is not always final. Learn how to check Section 58 defences, inspection records, prior reports, thresholds, and missing evidence.',
    category: 'rejections',
    readTime: '7 min',
    datePublished: '2026-04-09',
    dateModified: '2026-04-09',
    keywords: [
      'pothole claim rejected',
      'council rejected pothole claim',
      'Section 58 defence',
      'Freedom of Information request',
      'council inspection records',
    ],
    geoFocus: ['England', 'Wales', 'UK local councils'],
    image: '/blog/council-rejected-pothole-claim-1200.webp',
    imageAlt:
      'Driver reading a council rejection letter at a kitchen table after a pothole damage claim is refused.',
    imageWidth: 1200,
    imageHeight: 630,
  },
  {
    slug: 'spring-pothole-season',
    title: 'Spring Pothole Season: Why Winter Damage Shows Up Weeks Later',
    description:
      'Winter pothole impacts can cause delayed tyre, steering, suspension, and alignment damage. Learn how to spot spring symptoms and protect a delayed claim.',
    category: 'claims-advice',
    readTime: '7 min',
    datePublished: '2026-03-20',
    dateModified: '2026-03-20',
    keywords: [
      'spring pothole season',
      'delayed pothole damage',
      'wheel alignment damage',
      'suspension damage claim',
      'pothole impact damage',
    ],
    geoFocus: ['England', 'Wales', 'UK local councils'],
    image: '/blog/spring-pothole-season-1200.webp',
    imageAlt:
      'Mechanic inspecting a car suspension and underside on a ramp, showing hidden pothole damage that can appear weeks after impact.',
    imageWidth: 1200,
    imageHeight: 630,
  },
  {
    slug: 'what-evidence-do-you-need-for-pothole-damage-claim',
    title: 'What Evidence Do You Need for a Successful Pothole Damage Claim?',
    description:
      'A complete UK pothole damage claim evidence checklist covering pothole photos, vehicle damage, garage invoices, prior reports, and council records.',
    category: 'evidence',
    readTime: '7 min',
    datePublished: '2026-02-07',
    dateModified: '2026-02-07',
    keywords: [
      'pothole claim evidence',
      'pothole damage photos',
      'repair invoice',
      'council inspection records',
      'Section 58 defence',
    ],
    geoFocus: ['England', 'Wales', 'UK local councils'],
    image: '/blog/pothole-claim-evidence-1200.webp',
    imageAlt:
      'Driver photographing pothole damage beside a car wheel, showing evidence collection for a UK pothole damage claim.',
    imageWidth: 1200,
    imageHeight: 630,
  },
  {
    slug: 'new-year-same-potholes',
    title: 'New Year, Same Potholes: How to Protect Your Claim After Vehicle Damage',
    description:
      'January pothole damage is common after freeze-thaw weather. Learn what to photograph, what damage to check, and how to protect a UK pothole claim.',
    category: 'claims-advice',
    readTime: '6 min',
    datePublished: '2026-01-03',
    dateModified: '2026-01-03',
    keywords: [
      'pothole damage claim',
      'January potholes',
      'vehicle damage compensation',
      'council pothole claim',
      'Highways Act 1980',
    ],
    geoFocus: ['England', 'Wales', 'UK local councils', 'National Highways'],
    image: '/blog/new-year-same-potholes-1200.webp',
    imageAlt:
      'Rain-filled pothole on a residential UK road in winter, showing the kind of road defect that can cause vehicle damage.',
    imageWidth: 1200,
    imageHeight: 630,
  },
];
